import React, { useState, useEffect, useCallback } from 'react';
import { coursesApi } from '../../services/api';

// Language options
const LANGUAGE_OPTIONS = [
  { value: '', label: 'Alle Sprachen' },
  { value: 'Deutsch', label: 'Deutsch' },
  { value: 'Englisch', label: 'Englisch' },
  { value: 'Both', label: 'Deutsch und Englisch' },
];

// Degree options
const DEGREE_OPTIONS = [
  { value: '', label: 'Alle Abschlüsse' },
  { value: 'Bachelor', label: 'Bachelor' },
  { value: 'Master', label: 'Master' },
];

// Semester options
const SEMESTER_OPTIONS = [
  { value: '', label: 'Alle Semester' },
  { value: 'Wintersemester', label: 'Wintersemester' },
  { value: 'Sommersemester', label: 'Sommersemester' },
  { value: 'Both', label: 'Beide' },
];

// Rating filter options
const RATING_OPTIONS = [
  { value: '', label: 'Alle Bewertungen' },
  { value: '4', label: '4+ Sterne' },
  { value: '3', label: '3+ Sterne' },
  { value: '2', label: '2+ Sterne' },
  { value: '1', label: '1+ Sterne' },
];

function CourseManagement() {
  // Course list state
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    university: '',
    degree: '',
    studyProgram: '',
    semester: '',
    language: '',
    minRating: '',
  });
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [tempFilters, setTempFilters] = useState({ ...filters });

  // Dropdown options from API
  const [universities, setUniversities] = useState([]);
  const [studyPrograms, setStudyPrograms] = useState([]);
  const [filteredStudyPrograms, setFilteredStudyPrograms] = useState([]);
  const [formStudyPrograms, setFormStudyPrograms] = useState([]);

  // Modal state for add/edit course
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    enrolled_university: '',
    degree: 'Bachelor',
    study_programs: [],
    language: 'Deutsch',
    semester: 'Wintersemester',
  });
  const [formError, setFormError] = useState('');

  // Study program input for multi-select
  const [studyProgramInput, setStudyProgramInput] = useState('');
  const [showStudyProgramSuggestions, setShowStudyProgramSuggestions] = useState(false);

  // Issue #181: Import/Export state
  const [selectedCourses, setSelectedCourses] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  const perPage = 20;

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  // Fetch universities on mount
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await coursesApi.getUniversities();
        setUniversities(response.data.universities || []);
      } catch (err) {
        console.error('Error fetching universities:', err);
      }
    };
    fetchUniversities();
  }, []);

  // Fetch study programs on mount
  useEffect(() => {
    const fetchStudyPrograms = async () => {
      try {
        const response = await coursesApi.getStudyPrograms();
        setStudyPrograms(response.data.study_programs || []);
      } catch (err) {
        console.error('Error fetching study programs:', err);
      }
    };
    fetchStudyPrograms();
  }, []);

  // Filter study programs when university changes in filter
  useEffect(() => {
    const fetchFilteredPrograms = async () => {
      if (tempFilters.university) {
        try {
          const response = await coursesApi.getStudyPrograms(tempFilters.university);
          setFilteredStudyPrograms(response.data.study_programs || []);
        } catch (err) {
          console.error('Error fetching filtered study programs:', err);
          setFilteredStudyPrograms([]);
        }
      } else {
        setFilteredStudyPrograms(studyPrograms);
      }
    };
    fetchFilteredPrograms();
  }, [tempFilters.university, studyPrograms]);

  // Filter study programs when university changes in form (Issue: filter by university)
  useEffect(() => {
    const fetchFormStudyPrograms = async () => {
      if (formData.enrolled_university) {
        try {
          const response = await coursesApi.getStudyPrograms(formData.enrolled_university);
          setFormStudyPrograms(response.data.study_programs || []);
        } catch (err) {
          console.error('Error fetching form study programs:', err);
          setFormStudyPrograms([]);
        }
      } else {
        setFormStudyPrograms(studyPrograms);
      }
    };
    fetchFormStudyPrograms();
  }, [formData.enrolled_university, studyPrograms]);

  // Fetch courses
  const fetchCourses = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (filters.university) params.university = filters.university;
      if (filters.degree) params.degree = filters.degree;
      if (filters.studyProgram) params.study_program = filters.studyProgram;
      if (filters.semester) params.semester = filters.semester;
      if (filters.language) params.language = filters.language;
      if (filters.minRating) params.min_rating = parseFloat(filters.minRating);

      const response = await coursesApi.list(params);
      setCourses(response.data.courses || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError('Fehler beim Laden der Kurse');
      console.error('Courses error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters, search]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handleOpenFilterPopover = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    setShowFilterPopover(false);
  };

  const handleCancelFilters = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(false);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      university: '',
      degree: '',
      studyProgram: '',
      semester: '',
      language: '',
      minRating: '',
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setPage(1);
  };

  // Open modal for adding new course
  const handleAddCourse = () => {
    setEditingCourse(null);
    setFormData({
      name: '',
      code: '',
      enrolled_university: universities[0] || '',
      degree: 'Bachelor',
      study_programs: [],
      language: 'Deutsch',
      semester: 'Wintersemester',
    });
    setFormError('');
    setShowModal(true);
  };

  // Open modal for editing course
  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      code: course.code,
      enrolled_university: course.enrolled_university,
      degree: course.degree || 'Bachelor',
      study_programs: course.study_programs || [],
      language: course.language || 'Deutsch',
      semester: course.semester || 'Wintersemester',
    });
    setFormError('');
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setActionLoading(true);

    try {
      if (editingCourse) {
        // Update existing course
        await coursesApi.update(editingCourse.id, {
          name: formData.name,
          code: formData.code,
          enrolled_university: formData.enrolled_university,
          degree: formData.degree,
          study_programs: formData.study_programs,
          semester: formData.semester,
          language: formData.language,
        });
      } else {
        // Create new course
        await coursesApi.create(formData);
      }
      setShowModal(false);
      fetchCourses();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete course - show confirmation popover
  const handleDeleteCourse = (course) => {
    setCourseToDelete(course);
    setShowDeleteConfirm(true);
  };

  // Confirm delete course
  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;
    setActionLoading(true);
    try {
      await coursesApi.delete(courseToDelete.id);
      setShowDeleteConfirm(false);
      setCourseToDelete(null);
      fetchCourses();
    } catch (err) {
      alert('Löschen fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCourseToDelete(null);
  };

  // Add study program to form
  const handleAddStudyProgram = (program) => {
    if (program && !formData.study_programs.includes(program)) {
      setFormData({
        ...formData,
        study_programs: [...formData.study_programs, program],
      });
    }
    setStudyProgramInput('');
    setShowStudyProgramSuggestions(false);
  };

  // Remove study program from form
  const handleRemoveStudyProgram = (program) => {
    setFormData({
      ...formData,
      study_programs: formData.study_programs.filter(p => p !== program),
    });
  };

  // Filter suggestions for study program input (filtered by selected university)
  const getStudyProgramSuggestions = () => {
    if (!studyProgramInput) return [];
    return formStudyPrograms.filter(
      p => p.toLowerCase().includes(studyProgramInput.toLowerCase()) &&
           !formData.study_programs.includes(p)
    ).slice(0, 10);
  };

  // Format rating display
  const formatRating = (rating) => {
    if (!rating || rating === 0) return '-';
    return rating.toFixed(1);
  };

  // Get language display label
  const getLanguageLabel = (language) => {
    const option = LANGUAGE_OPTIONS.find(o => o.value === language);
    return option ? option.label : language || '-';
  };

  // Abbreviate semester for table display
  const getSemesterShort = (semester) => {
    if (!semester) return '-';
    if (semester === 'Wintersemester') return 'Winter';
    if (semester === 'Sommersemester') return 'Sommer';
    if (semester === 'Both') return 'Beide';
    return semester;
  };
  const totalPages = Math.ceil(total / perPage);

  // Issue #181: Selection handlers
  const handleSelectAll = async () => {
    // If any courses are selected, deselect all
    if (selectedCourses.size > 0) {
      setSelectedCourses(new Set());
      return;
    }

    // Otherwise, select all courses
    try {
      const baseParams = {};
      if (search) baseParams.search = search;
      if (filters.university) baseParams.university = filters.university;
      if (filters.degree) baseParams.degree = filters.degree;
      if (filters.studyProgram) baseParams.study_program = filters.studyProgram;
      if (filters.semester) baseParams.semester = filters.semester;
      if (filters.language) baseParams.language = filters.language;
      if (filters.minRating) baseParams.min_rating = parseFloat(filters.minRating);

      const allCourseIds = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await coursesApi.list({ ...baseParams, page: currentPage, per_page: 100 });
        const coursesData = response.data.courses || [];
        allCourseIds.push(...coursesData.map(c => c.id));
        totalPages = Math.ceil(response.data.total / 100);
        currentPage++;
      } while (currentPage <= totalPages);

      setSelectedCourses(new Set(allCourseIds));
    } catch (err) {
      console.error('Error fetching all courses:', err);
      setSelectedCourses(new Set(courses.map(c => c.id)));
    }
  };

  const handleSelectCourse = (courseId) => {
    const newSelection = new Set(selectedCourses);
    if (newSelection.has(courseId)) {
      newSelection.delete(courseId);
    } else {
      newSelection.add(courseId);
    }
    setSelectedCourses(newSelection);
  };

  // Issue #181: Export CSV functionality
  const handleExportCSV = async () => {
    if (selectedCourses.size === 0) {
      alert('Keine Kurse zum Exportieren ausgewählt');
      return;
    }

    try {
      // Fetch all selected courses data (backend limits to 100 per page)
      const baseParams = {};
      if (search) baseParams.search = search;
      if (filters.university) baseParams.university = filters.university;
      if (filters.degree) baseParams.degree = filters.degree;
      if (filters.studyProgram) baseParams.study_program = filters.studyProgram;
      if (filters.semester) baseParams.semester = filters.semester;
      if (filters.language) baseParams.language = filters.language;
      if (filters.minRating) baseParams.min_rating = parseFloat(filters.minRating);

      const allCourses = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await coursesApi.list({ ...baseParams, page: currentPage, per_page: 100 });
        const coursesData = response.data.courses || [];
        allCourses.push(...coursesData);
        totalPages = Math.ceil(response.data.total / 100);
        currentPage++;
      } while (currentPage <= totalPages);

      const coursesToExport = allCourses.filter(c => selectedCourses.has(c.id));

      if (coursesToExport.length === 0) {
        alert('Keine Kurse zum Exportieren vorhanden');
        return;
      }

      const headers = ['Hochschule', 'Kursname', 'Code', 'Abschluss', 'Studiengänge', 'Semester', 'Sprache', 'Bewertung'];
      const rows = coursesToExport.map(course => [
        course.enrolled_university || '',
        course.name || '',
        course.code || '',
        course.degree || '',
        (course.study_programs || []).join('; '),
        course.semester || '',
        course.language || '',
        course.average_rating ? course.average_rating.toFixed(1) : '',
      ]);

      const csvContent = [
        headers.join(';'),
        ...rows.map(row => row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(';'))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'kurse_export_' + new Date().toISOString().split('T')[0] + '.csv';
      link.click();
    } catch (err) {
      console.error('Export error:', err);
      alert('Export fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    }
  };

  // Issue #181: Import CSV functionality
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('CSV-Datei ist leer oder hat keine Datenzeilen');
          return;
        }

        const firstLine = lines[0];
        const delimiter = firstLine.includes(';') ? ';' : ',';

        const coursesToImport = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          const values = [];
          let current = '';
          let inQuotes = false;
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === delimiter && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim());

          if (values.length >= 5) {
            coursesToImport.push({
              enrolled_university: values[0] || '',
              name: values[1] || '',
              study_program: values[4] || values[2] || '',
              semester: values[5] || '',
              language: values[6] || '',
              degree: values[3] || 'Bachelor',
              code: values[2] || null,
            });
          }
        }

        if (coursesToImport.length === 0) {
          alert('Keine gültigen Kurse in der CSV-Datei gefunden');
          return;
        }

        setImportLoading(true);
        setShowImportModal(true);

        const response = await coursesApi.importCourses(coursesToImport);
        setImportResult(response.data);
        fetchCourses();
      } catch (err) {
        setImportResult({
          success: false,
          message: 'Import fehlgeschlagen: ' + (err.response?.data?.message || err.message),
          imported_count: 0,
          skipped_count: 0,
          errors: [err.message],
        });
      } finally {
        setImportLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Kursverwaltung</h1>
        <div className="page-header-actions">
          <div className="header-buttons">
            <button className="secondary-button" onClick={handleExportCSV} disabled={selectedCourses.size === 0} title={selectedCourses.size > 0 ? `${selectedCourses.size} ausgewählte exportieren` : 'Kurse auswählen zum Exportieren'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              CSV Export
            </button>
            <label className="secondary-button import-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              CSV Import
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                style={{ display: 'none' }}
              />
            </label>
            <button className="primary-button" onClick={handleAddCourse}>
              + Neuer Kurs
            </button>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kursname oder Code suchen..."
            className="search-input"
            aria-label="Kurse suchen"
          />
          <button type="submit" className="search-button">Suchen</button>
        </form>

        <div className="filter-controls">
          <button
            type="button"
            className="filter-button"
            onClick={handleOpenFilterPopover}
            aria-label="Filter öffnen"
            aria-expanded={showFilterPopover}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Filter
            {activeFilterCount > 0 && (
              <span className="filter-count-badge" aria-label={`${activeFilterCount} aktive Filter`}>{activeFilterCount}</span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="reset-filter-button"
              onClick={handleResetFilters}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Filter Popover */}
      {showFilterPopover && (
        <div className="filter-popover-overlay" onClick={handleCancelFilters} role="dialog" aria-modal="true" aria-labelledby="filter-title">
          <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
            <div className="filter-popover-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <span className="filter-title" id="filter-title">Filter</span>
            </div>

            <div className="filter-popover-content">
              {/* University Filter */}
              <div className="filter-section">
                <label className="filter-section-label" htmlFor="filter-university">Hochschule</label>
                <select
                  id="filter-university"
                  className="filter-input"
                  value={tempFilters.university}
                  onChange={(e) => {
                    setTempFilters({ ...tempFilters, university: e.target.value, studyProgram: '' });
                  }}
                >
                  <option value="">Alle Hochschulen</option>
                  {universities.map((uni, index) => (
                    <option key={index} value={uni}>{uni}</option>
                  ))}
                </select>
              </div>

              {/* Degree Filter */}
              <div className="filter-section">
                <label className="filter-section-label" htmlFor="filter-degree">Abschluss</label>
                <select
                  id="filter-degree"
                  className="filter-input"
                  value={tempFilters.degree}
                  onChange={(e) => setTempFilters({ ...tempFilters, degree: e.target.value })}
                >
                  {DEGREE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Study Program Filter */}
              <div className="filter-section">
                <label className="filter-section-label" htmlFor="filter-study-program">Studiengang</label>
                <select
                  id="filter-study-program"
                  className="filter-input"
                  value={tempFilters.studyProgram}
                  onChange={(e) => setTempFilters({ ...tempFilters, studyProgram: e.target.value })}
                >
                  <option value="">Alle Studiengänge</option>
                  {filteredStudyPrograms.map((program, index) => (
                    <option key={index} value={program}>{program}</option>
                  ))}
                </select>
              </div>

              {/* Semester Filter */}
              <div className="filter-section">
                <label className="filter-section-label" htmlFor="filter-semester">Semester</label>
                <select
                  id="filter-semester"
                  className="filter-input"
                  value={tempFilters.semester}
                  onChange={(e) => setTempFilters({ ...tempFilters, semester: e.target.value })}
                >
                  {SEMESTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Language Filter */}
              <div className="filter-section">
                <label className="filter-section-label" htmlFor="filter-language">Sprache</label>
                <select
                  id="filter-language"
                  className="filter-input"
                  value={tempFilters.language}
                  onChange={(e) => setTempFilters({ ...tempFilters, language: e.target.value })}
                >
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Rating Filter */}
              <div className="filter-section">
                <label className="filter-section-label" htmlFor="filter-rating">Mindestbewertung</label>
                <select
                  id="filter-rating"
                  className="filter-input"
                  value={tempFilters.minRating}
                  onChange={(e) => setTempFilters({ ...tempFilters, minRating: e.target.value })}
                >
                  {RATING_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Buttons */}
              <div className="filter-popover-buttons">
                <button type="button" className="secondary-button" onClick={handleCancelFilters}>
                  Abbrechen
                </button>
                <button type="button" className="primary-button" onClick={handleApplyFilters}>
                  Anwenden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Course Modal */}
      {showModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div className="modal">
            <h2 id="modal-title">{editingCourse ? 'Kurs bearbeiten' : 'Neuer Kurs'}</h2>
            <form onSubmit={handleSubmit}>
              {formError && <div className="error-message" role="alert">{formError}</div>}

              <div className="form-group">
                <label htmlFor="course-name">Kursname *</label>
                <input
                  type="text"
                  id="course-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Kursname eingeben"
                />
              </div>

              <div className="form-group">
                <label htmlFor="course-code">Kurscode</label>
                <input
                  type="text"
                  id="course-code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Kurscode eingeben"
                />
              </div>

              <div className="form-group">
                <label htmlFor="course-university">Hochschule *</label>
                <select
                  id="course-university"
                  value={formData.enrolled_university}
                  onChange={(e) => setFormData({ ...formData, enrolled_university: e.target.value, study_programs: [] })}
                >
                  <option value="">Bitte wählen...</option>
                  {universities.map((uni, index) => (
                    <option key={index} value={uni}>{uni}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="course-degree">Abschluss</label>
                <select
                  id="course-degree"
                  value={formData.degree}
                  onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                >
                  <option value="Bachelor">Bachelor</option>
                  <option value="Master">Master</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="course-study-programs">Studiengänge</label>
                <div className="multi-select-container">
                  <div className="selected-items">
                    {formData.study_programs.map((program, index) => (
                      <span key={index} className="selected-tag">
                        {program}
                        <button
                          type="button"
                          className="remove-tag-button"
                          onClick={() => handleRemoveStudyProgram(program)}
                          aria-label={`${program} entfernen`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="autocomplete-container">
                    <input
                      type="text"
                      id="course-study-programs"
                      className="filter-input"
                      placeholder="Studiengang eingeben..."
                      value={studyProgramInput}
                      onChange={(e) => {
                        setStudyProgramInput(e.target.value);
                        setShowStudyProgramSuggestions(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowStudyProgramSuggestions(studyProgramInput.length > 0)}
                      onBlur={() => setTimeout(() => setShowStudyProgramSuggestions(false), 200)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (studyProgramInput.trim()) {
                            handleAddStudyProgram(studyProgramInput.trim());
                          }
                        }
                      }}
                    />
                    {showStudyProgramSuggestions && getStudyProgramSuggestions().length > 0 && (
                      <ul className="autocomplete-suggestions" role="listbox">
                        {getStudyProgramSuggestions().map((program, index) => (
                          <li
                            key={index}
                            className="autocomplete-suggestion"
                            onClick={() => handleAddStudyProgram(program)}
                            role="option"
                          >
                            {program}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="course-language">Sprache</label>
                <select
                  id="course-language"
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                >
                  <option value="Deutsch">Deutsch</option>
                  <option value="Englisch">Englisch</option>
                  <option value="Both">Deutsch und Englisch</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="course-semester">Semester</label>
                <select
                  id="course-semester"
                  value={formData.semester}
                  onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                >
                  <option value="Wintersemester">Wintersemester</option>
                  <option value="Sommersemester">Sommersemester</option>
                  <option value="Both">Beide</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="secondary-button" onClick={() => setShowModal(false)} disabled={actionLoading}>
                  Abbrechen
                </button>
                <button type="submit" className="primary-button" disabled={actionLoading}>
                  {actionLoading ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="results-count">
        <span>{total} Kurse gefunden</span>
        {selectedCourses.size > 0 && (
          <span className="selected-count">{selectedCourses.size} Kurse ausgewählt</span>
        )}
      </div>

      {isLoading ? (
        <div className="loading-container" aria-label="Kurse werden geladen">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table" role="grid">
              <thead>
                <tr>
                  <th scope="col" className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedCourses.size > 0}
                      onChange={handleSelectAll}
                      aria-label="Alle Kurse auswählen"
                    />
                  </th>
                  <th scope="col">Kursname</th>
                  <th scope="col">Code</th>
                  <th scope="col">Hochschule</th>
                  <th scope="col">Abschluss</th>
                  <th scope="col">Studiengänge</th>
                  <th scope="col">Semester</th>
                  <th scope="col">Sprache</th>
                  <th scope="col">Bewertung</th>
                  <th scope="col">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {courses.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-table-message">
                      {activeFilterCount > 0 ? 'Keine Kurse gefunden mit den gewählten Filtern' : 'Keine Kurse vorhanden'}
                    </td>
                  </tr>
                ) : (
                  courses.map((course) => (
                    <tr key={course.id} className={selectedCourses.has(course.id) ? 'selected-row' : ''}>
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={selectedCourses.has(course.id)}
                          onChange={() => handleSelectCourse(course.id)}
                          aria-label={course.name + ' auswählen'}
                        />
                      </td>
                      <td>{course.name}</td>
                      <td>{course.code}</td>
                      <td>{course.enrolled_university}</td>
                      <td>{course.degree || '-'}</td>
                      <td>
                        <div className="study-programs-cell">
                          {course.study_programs && course.study_programs.length > 0
                            ? course.study_programs.join(', ')
                            : '-'
                          }
                        </div>
                      </td>
                      <td>{getSemesterShort(course.semester)}</td>
                      <td>{getLanguageLabel(course.language)}</td>
                      <td>
                        <span className="rating-display">
                          {formatRating(course.average_rating)}
                          {course.review_count > 0 && (
                            <span className="review-count"> ({course.review_count})</span>
                          )}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            className="icon-button edit-button"
                            onClick={() => handleEditCourse(course)}
                            disabled={actionLoading}
                            title="Bearbeiten"
                            aria-label={`${course.name} bearbeiten`}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button
                            className="icon-button delete-button"
                            onClick={() => handleDeleteCourse(course)}
                            disabled={actionLoading}
                            title="Löschen"
                            aria-label={`${course.name} löschen`}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Seitennavigation">
              <button
                className="pagination-button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Vorherige Seite"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
              <span className="pagination-info" aria-current="page">
                Seite {page} von {totalPages}
              </span>
              <button
                className="pagination-button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                aria-label="Nächste Seite"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
          )}
        </>
      )}

      {/* Issue #181: Import Result Modal */}
      {/* Issue #184: Updated to show duplicate courses that were skipped */}
      {showImportModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="import-modal-title">
          <div className="modal">
            <h2 id="import-modal-title">CSV Import Ergebnis</h2>
            {importLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Importiere Kurse...</p>
              </div>
            ) : importResult && (
              <>
                <div className={importResult.success ? 'import-result success' : 'import-result error'}>
                  <ul>
                    <li>Importiert: {importResult.imported_count}</li>
                    <li>Übersprungen: {importResult.skipped_count}</li>
                  </ul>
                  {/* Issue #184: Show duplicate courses that could not be imported */}
                  {importResult.duplicate_courses && importResult.duplicate_courses.length > 0 && (
                    <div className="import-duplicates" role="alert">
                      <p><strong>Folgende Kurse existieren bereits und wurden nicht importiert:</strong></p>
                      <ul>
                        {importResult.duplicate_courses.slice(0, 20).map((course, i) => (
                          <li key={i}>{course}</li>
                        ))}
                        {importResult.duplicate_courses.length > 20 && (
                          <li>...und {importResult.duplicate_courses.length - 20} weitere Kurse</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="import-errors">
                      <p><strong>Fehler:</strong></p>
                      <ul>
                        {importResult.errors.slice(0, 10).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importResult.errors.length > 10 && (
                          <li>...und {importResult.errors.length - 10} weitere Fehler</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <button type="button" className="primary-button" onClick={() => { setShowImportModal(false); setImportResult(null); }}>
                    Okay
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Popover */}
      {showDeleteConfirm && courseToDelete && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="modal delete-confirm-modal">
            <div className="delete-confirm-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#e74c3c">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h2 id="delete-confirm-title">Kurs löschen?</h2>
            <p className="delete-confirm-message">
              Möchten Sie den Kurs <strong>"{courseToDelete.name}"</strong><br />wirklich löschen?
            </p>
            <p className="delete-confirm-warning">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="delete-confirm-actions">
              <button type="button" className="secondary-button" onClick={cancelDelete} disabled={actionLoading}>
                Abbrechen
              </button>
              <button type="button" className="danger-button" onClick={confirmDeleteCourse} disabled={actionLoading}>
                {actionLoading ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseManagement;
